// Humanize raw category keys like `amenity_specific` → `Amenity Specific`.
// Used as a defensive fallback wherever the backend may not have supplied a
// `*_display` field. Prefer the display field when it exists; only call this
// on the raw key as a last resort.

export function prettifyCategory(value) {
  if (value == null) return value;
  const str = String(value).trim();
  if (!str) return str;
  return str
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Known acronyms / special-cased words that don't follow default Title Case
// rules. Keep this list small and additive — anything not here gets default
// Title Case ("tv" → "Tv", "rv" → "RV" only because it's listed).
const KNOWN_ACRONYMS = {
  rv: 'RV',
  wifi: 'Wi-Fi',
};

// Full-key overrides where the default has_/is_/num_ stripping would lose
// meaning. `has_wifi` is meaningful as "Has Wi-Fi" (boolean about Wi-Fi
// availability) rather than the bare "Wi-Fi" (which reads like a category).
const KNOWN_KEYS = {
  has_wifi: 'Has Wi-Fi',
};

function titleCaseWord(word) {
  if (!word) return word;
  const acronym = KNOWN_ACRONYMS[word.toLowerCase()];
  if (acronym) return acronym;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

// Humanize raw amenity / fact-key identifiers used in Ground Truth and
// client-priority surfaces (markup #101). Raw fact_keys carry shape like
// `has_hot_tubs` / `num_rv_sites` / `pet_friendly`; backend usually ships a
// `fact_key_display`, but this function is the fallback when it doesn't.
//
// Rules:
//   - `has_X` / `is_X` / `num_X` → strip the boolean/count prefix, Title
//                                   Case the rest. "Num" is dev shorthand
//                                   per markup #101.
//   - Already-cased "Num X"      → strip the leading "Num " word.
//   - Otherwise                  → snake → Title Case.
//   - Already-cased strings (no `_`) pass through unchanged so acronyms
//     like "RV Sites" don't get lower-cased to "Rv Sites".
//
// Examples:
//   has_hot_tubs → Hot Tubs            num_rv_sites → RV Sites
//   has_wifi    → Wi-Fi                num_hot_tubs → Hot Tubs
//   wifi_type   → Wi-Fi Type           dog_park_size → Dog Park Size
//   pet_friendly → Pet Friendly        Num RV Sites → RV Sites
//                                       RV Sites    → RV Sites (untouched)
export function prettifyAmenityLabel(value) {
  if (value == null) return value;
  const str = String(value).trim();
  if (!str) return str;
  // Explicit full-key override takes precedence over generic rules.
  const override = KNOWN_KEYS[str.toLowerCase()];
  if (override) return override;
  if (/_/.test(str)) {
    return str
      .replace(/^(has|is|num)_/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .split(' ')
      .map(titleCaseWord)
      .join(' ');
  }
  // All-lowercase space-separated strings (e.g. "splash pad", "hot tubs")
  // — title-case each word. Skip if any uppercase already present so acronyms
  // like "RV Sites" pass through untouched.
  if (!/[A-Z]/.test(str)) {
    return str
      .replace(/\s+/g, ' ')
      .split(' ')
      .map(titleCaseWord)
      .join(' ');
  }
  // Strip the cased "Num " prefix used in already-display labels.
  return str.replace(/^Num\s+/i, '');
}
